// One hook for every pointer gesture the app supports on a surface: tap, double tap, long press
// (touch hold, mouse hold, right-click and the keyboard's menu key), and swipes in four directions.
// Continuous drags (rows that follow the finger, sheets, pull-to-refresh) use `onDrag`. Built on
// @use-gesture's drag for pointer capture, axis locking and swipe velocity; the timers for long
// press and double tap are ours. Gestures are shortcuts: callers must keep a tap/keyboard twin.
import { useDrag } from "@use-gesture/react";
import { useCallback, useEffect, useRef } from "react";
import { haptic } from "./haptics";

export type SwipeDirection = "left" | "right" | "up" | "down";

export interface DragState {
  /** Pixels moved from where the pointer went down. */
  dx: number;
  dy: number;
  /** Pixels per millisecond at release. */
  vx: number;
  vy: number;
  active: boolean;
}

export interface GestureHandlers {
  /** Activation: a click, a tap, Enter or Space. Delayed by `doubleTapMs` when `onDoubleTap` is set. */
  onTap?: (() => void) | undefined;
  onDoubleTap?: (() => void) | undefined;
  /** Held for `longPressMs`, right-clicked, or the keyboard's menu key. */
  onLongPress?: (() => void) | undefined;
  onSwipe?: Partial<Record<SwipeDirection, () => void>> | undefined;
  onDrag?: ((state: DragState) => void) | undefined;
}

export interface GestureOptions {
  /** Which way a drag may go; the browser keeps the other axis for scrolling. */
  axis?: "x" | "y" | "both";
  longPressMs?: number;
  doubleTapMs?: number;
  /** Pixels a swipe must cover (with enough speed) or exceed on its own. */
  swipeDistance?: number;
  disabled?: boolean;
}

const LONG_PRESS_MS = 500;
const DOUBLE_TAP_MS = 280;
const SWIPE_PX = 48;
/** Movement that turns a press into a drag: it cancels a long press and swallows the click. */
const SLOP_PX = 10;

/** The two methods the handlers use; Tamagui types its events its own way, so this is the common part. */
interface SuppressibleEvent {
  preventDefault: () => void;
  stopPropagation: () => void;
}

/** What to spread onto the element: the drag's own listeners plus ours. */
export type GestureBindings = Pick<
  React.DOMAttributes<HTMLElement>,
  | "onPointerDown"
  | "onPointerMove"
  | "onPointerUp"
  | "onPointerCancel"
  | "onGotPointerCapture"
  | "onLostPointerCapture"
> & {
  onClick: () => void;
  onClickCapture: (event: SuppressibleEvent) => void;
  onContextMenu: (event: SuppressibleEvent) => void;
  onKeyDown: (event: SuppressibleEvent & { key: string; shiftKey: boolean }) => void;
  /** Tells the browser which pans it may keep: a horizontal row leaves vertical scrolling alone. */
  style: { touchAction: string };
};

export function useGestures(
  handlers: GestureHandlers,
  options: GestureOptions = {},
): GestureBindings {
  const {
    axis = "both",
    longPressMs = LONG_PRESS_MS,
    doubleTapMs = DOUBLE_TAP_MS,
    swipeDistance = SWIPE_PX,
    disabled = false,
  } = options;
  const latest = useRef(handlers);
  useEffect(() => {
    latest.current = handlers;
  });

  const holdTimer = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  const tapTimer = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  const lastTap = useRef(0);
  /** A long press or drag happened during this press: the click that follows must not act. */
  const swallow = useRef(false);
  const menuAt = useRef(0);

  const clearHold = useCallback(() => {
    clearTimeout(holdTimer.current);
    holdTimer.current = undefined;
  }, []);
  useEffect(
    () => () => {
      clearHold();
      clearTimeout(tapTimer.current);
    },
    [clearHold],
  );

  const fireLongPress = useCallback(() => {
    if (latest.current.onLongPress === undefined) return;
    menuAt.current = Date.now();
    haptic("select");
    latest.current.onLongPress();
  }, []);

  const bindDrag = useDrag(
    ({
      movement: [mx, my],
      velocity: [vx, vy],
      direction: [dx, dy],
      active,
      first,
      last,
      swipe: [sx, sy],
      distance,
    }) => {
      if (first) swallow.current = false;
      if (Math.hypot(mx, my) > SLOP_PX) {
        clearHold();
        swallow.current = true;
      }
      latest.current.onDrag?.({ dx: mx, dy: my, vx: vx * dx, vy: vy * dy, active });
      if (!last) return;
      const swipes = latest.current.onSwipe;
      if (swipes === undefined) return;
      const [px, py] = distance;
      // A swipe is a quick flick (use-gesture's own detection) or a long deliberate drag.
      const dir: SwipeDirection | undefined =
        sx !== 0
          ? sx > 0
            ? "right"
            : "left"
          : sy !== 0
            ? sy > 0
              ? "down"
              : "up"
            : Math.abs(mx) >= swipeDistance * 2 && px >= py
              ? mx > 0
                ? "right"
                : "left"
              : Math.abs(my) >= swipeDistance * 2 && py > px
                ? my > 0
                  ? "down"
                  : "up"
                : undefined;
      if (dir !== undefined) {
        haptic("select");
        const swipe =
          dir === "left"
            ? swipes.left
            : dir === "right"
              ? swipes.right
              : dir === "up"
                ? swipes.up
                : swipes.down;
        swipe?.();
      }
    },
    {
      enabled: !disabled,
      filterTaps: true,
      tapsThreshold: SLOP_PX,
      ...(axis === "both" ? {} : { axis }),
      swipe: { distance: swipeDistance, velocity: 0.35, duration: 700 },
    },
  );
  const drag = bindDrag();

  const onPointerDown = useCallback(
    (event: React.PointerEvent): void => {
      if (disabled) return;
      drag.onPointerDown?.(event);
      if (event.button !== 0 || latest.current.onLongPress === undefined) return;
      clearHold();
      swallow.current = false;
      holdTimer.current = setTimeout(() => {
        holdTimer.current = undefined;
        swallow.current = true;
        fireLongPress();
      }, longPressMs);
    },
    [clearHold, disabled, drag, fireLongPress, longPressMs],
  );

  const end = useCallback(
    (event: React.PointerEvent, key: "onPointerUp" | "onPointerCancel"): void => {
      clearHold();
      if (key === "onPointerUp") drag.onPointerUp?.(event);
      else drag.onPointerCancel?.(event);
    },
    [clearHold, drag],
  );

  const onClickCapture = useCallback((event: SuppressibleEvent): void => {
    if (!swallow.current) return;
    swallow.current = false;
    event.preventDefault();
    event.stopPropagation();
  }, []);

  const onClick = useCallback((): void => {
    if (disabled) return;
    const { onTap, onDoubleTap } = latest.current;
    if (onDoubleTap === undefined) {
      onTap?.();
      return;
    }
    const now = Date.now();
    if (now - lastTap.current <= doubleTapMs) {
      clearTimeout(tapTimer.current);
      lastTap.current = 0;
      haptic("commit");
      onDoubleTap();
      return;
    }
    lastTap.current = now;
    tapTimer.current = setTimeout(() => {
      lastTap.current = 0;
      latest.current.onTap?.();
    }, doubleTapMs);
  }, [disabled, doubleTapMs]);

  const onContextMenu = useCallback(
    (event: SuppressibleEvent): void => {
      if (disabled || latest.current.onLongPress === undefined) return;
      event.preventDefault();
      // Touch browsers send a contextmenu after their own long press; ours already fired.
      if (Date.now() - menuAt.current < longPressMs * 2) return;
      fireLongPress();
    },
    [disabled, fireLongPress, longPressMs],
  );

  const onKeyDown = useCallback(
    (event: SuppressibleEvent & { key: string; shiftKey: boolean }): void => {
      if (disabled || latest.current.onLongPress === undefined) return;
      if (event.key === "ContextMenu" || (event.key === "F10" && event.shiftKey)) {
        event.preventDefault();
        fireLongPress();
      }
    },
    [disabled, fireLongPress],
  );

  return {
    ...drag,
    onPointerDown,
    onPointerUp: (event: React.PointerEvent) => end(event, "onPointerUp"),
    onPointerCancel: (event: React.PointerEvent) => end(event, "onPointerCancel"),
    onClick,
    onClickCapture,
    onContextMenu,
    onKeyDown,
    style: { touchAction: axis === "x" ? "pan-y" : axis === "y" ? "pan-x" : "none" },
  };
}
